import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Check, Clock, Star, Building2, BedDouble, Plane, ArrowLeft, ArrowRight, Gift, Info, Phone, MapPin, CalendarDays } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useReveal } from "../hooks/useReveal";
import { useContent } from "../context/ContentContext";
import { formatPrice } from "../lib/api";
import { usePageMeta } from "../hooks/usePageMeta";

const PackageDetail = () => {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const { PACKAGES, CONTACT, HAJJ_GIFTS, HAJJ_BOOKING } = useContent();
  const navigate = useNavigate();
  useReveal([slug, lang]);
  const en = lang === "en";

  const pkg = PACKAGES.find((p) => p.slug === slug);
  usePageMeta({ title: pkg ? (lang === "en" ? pkg.name_en : pkg.name_bn) : "Package", description: pkg ? `${pkg.tagline_en || ""} ${pkg.duration} days · ${pkg.makkah_hotel} · from ${pkg.currency === "BDT" ? "৳" : "$"}${Number(pkg.price).toLocaleString("en-IN")} per person. Book with Fly Jannah Tours & Travels, Dhaka.` : "", image: pkg?.image });
  if (!pkg) {
    return (
      <div className="pt-24 pb-20 text-center">
        <p className="text-xl">Package not found.</p>
        <Link to="/umrah" className="text-jannah-green underline mt-4 inline-block">Back to packages</Link>
      </div>
    );
  }

  const name = en ? pkg.name_en : pkg.name_bn;
  const includes = en ? pkg.includes_en : pkg.includes_bn;
  const tagline = en ? pkg.tagline_en : pkg.tagline_bn;
  const notes = (en ? pkg.notes_en : pkg.notes_bn) || [];
  const isHajj = pkg.type === "hajj";

  const info = [
    { icon: Building2, label: en ? "Makkah Hotel" : "মক্কা হোটেল", val: pkg.makkah_hotel, sub: pkg.makkah_dist },
    { icon: Building2, label: en ? "Madinah Hotel" : "মদিনা হোটেল", val: pkg.madinah_hotel, sub: pkg.madinah_dist },
    { icon: Clock, label: t.duration, val: `${pkg.duration} ${t.days}`, sub: en ? pkg.flight_en : pkg.flight_bn },
    { icon: BedDouble, label: en ? "Category" : "ক্যাটাগরি", val: pkg.tier, sub: en ? pkg.room_en : pkg.room_bn },
  ];

  const related = PACKAGES.filter((p) => p.type === pkg.type && p.id !== pkg.id).slice(0, 3);

  return (
    <div data-testid="package-detail">
      <section className="relative pt-24 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${pkg.image})` }} />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-6">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-5">
            <ArrowLeft size={17} /> {en ? "Back" : "পিছনে"}
          </button>
          <span className="px-3 py-1 rounded-full btn-gold text-xs font-bold uppercase">{pkg.tier} · {pkg.type}</span>
          <h1 className="mt-4 text-4xl md:text-6xl font-bold text-white" data-testid="package-title">{name}</h1>
          <p className="mt-3 text-white/80 text-lg">{tagline}</p>
          <div className="mt-4 flex items-center gap-2 text-white">
            <Star size={18} className="fill-jannah-gold text-jannah-gold" /> {pkg.rating.toFixed(1)}
          </div>
        </div>
      </section>

      <section className="section-pad bg-jannah-cream">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-10">
            <div className="grid sm:grid-cols-2 gap-4 reveal">
              {info.map((it, i) => {
                const Icon = it.icon;
                return (
                  <div key={i} className="bg-white rounded-xl p-5 border border-jannah-sand flex gap-4">
                    <div className="w-11 h-11 rounded-full bg-jannah-green/10 flex items-center justify-center shrink-0">
                      <Icon className="text-jannah-green" size={20} />
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground uppercase tracking-wide">{it.label}</div>
                      <div className="font-semibold text-jannah-green-dark">{it.val}</div>
                      {it.sub && <div className="text-sm text-jannah-gold">{it.sub}</div>}
                    </div>
                  </div>
                );
              })}
            </div>

            {pkg.stays && (
              <div className="reveal" data-testid="package-stays">
                <h2 className="text-3xl font-bold text-jannah-ink mb-6">{en ? "Accommodation Plan" : "অবস্থানের পরিকল্পনা"}</h2>
                <div className="relative border-l-2 border-jannah-gold/50 ml-3 space-y-6">
                  {pkg.stays.map((s, i) => (
                    <div key={i} className="relative pl-8">
                      <span className="absolute -left-[11px] top-1.5 w-5 h-5 rounded-full bg-jannah-gold border-4 border-jannah-cream" />
                      <div className="bg-white rounded-xl p-5 border border-jannah-sand">
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="inline-flex items-center gap-1.5 font-bold text-jannah-green text-lg"><MapPin size={16} /> {en ? s.city_en : s.city_bn}</span>
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-jannah-green/10 text-jannah-green"><CalendarDays size={13} /> {en ? s.days_en : s.days_bn}</span>
                        </div>
                        <p className="mt-2 text-jannah-ink/75 leading-relaxed">{en ? s.desc_en : s.desc_bn}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="reveal">
              <h2 className="text-3xl font-bold text-jannah-ink mb-6">{t.includes}</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {includes.map((inc, i) => (
                  <div key={i} className="flex items-start gap-3 bg-white rounded-lg p-4 border border-jannah-sand">
                    <Check size={18} className="text-jannah-green shrink-0 mt-0.5" />
                    <span className="text-jannah-ink/80">{inc}</span>
                  </div>
                ))}
              </div>
            </div>

            {isHajj && (
              <div className="reveal bg-jannah-green-dark rounded-2xl p-8 text-white relative overflow-hidden" data-testid="package-gifts">
                <div className="absolute inset-0 pattern-overlay opacity-30" />
                <div className="relative">
                  <h2 className="text-2xl md:text-3xl font-bold text-jannah-gold flex items-center gap-3"><Gift size={26} /> {en ? "Free Gifts (Hadiya) with Every Package" : "উভয় প্যাকেজে ফ্রি হাদিয়া"}</h2>
                  <div className="mt-6 flex flex-wrap gap-2.5">
                    {HAJJ_GIFTS[lang].map((g, i) => (
                      <span key={i} className="px-3.5 py-1.5 rounded-full bg-white/10 border border-white/15 text-sm">{g}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {notes.length > 0 && (
              <div className="reveal bg-jannah-gold/10 border border-jannah-gold/40 rounded-2xl p-6" data-testid="package-notes">
                <h3 className="font-bold text-jannah-ink flex items-center gap-2 mb-3"><Info size={18} className="text-jannah-gold" /> {en ? "Please Note" : "বিঃদ্রঃ"}</h3>
                <ul className="space-y-2 text-jannah-ink/80 text-sm leading-relaxed">
                  {notes.map((n, i) => <li key={i} className="flex gap-2"><span className="text-jannah-gold">•</span>{n}</li>)}
                </ul>
              </div>
            )}
          </div>

          <div className="reveal">
            <div className="sticky top-32 bg-white rounded-2xl border border-jannah-sand p-8 shadow-lg" data-testid="package-booking-card">
              <div className="text-sm text-muted-foreground">{t.from}</div>
              <div className="text-4xl font-bold text-jannah-green" data-testid="package-price">{formatPrice(pkg)}</div>
              <div className="text-sm text-muted-foreground">{t.per_person}</div>
              {pkg.price_note_en && <div className="mt-1 text-xs text-jannah-gold font-medium mb-5">({en ? pkg.price_note_en : pkg.price_note_bn})</div>}
              <div className="space-y-3 text-sm border-t border-jannah-sand pt-5">
                <div className="flex justify-between"><span className="text-muted-foreground">{t.duration}</span><span className="font-semibold text-jannah-green-dark">{pkg.duration} {t.days}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{en ? "Type" : "ধরন"}</span><span className="font-semibold text-jannah-green-dark capitalize">{pkg.type}</span></div>
                {pkg.year && <div className="flex justify-between"><span className="text-muted-foreground">{en ? "Season" : "মৌসুম"}</span><span className="font-semibold text-jannah-green-dark">Hajj {pkg.year}</span></div>}
                <div className="flex items-center gap-2 text-jannah-green"><Plane size={15} /> {en ? "Direct return flights included" : "ডিরেক্ট রিটার্ন ফ্লাইট অন্তর্ভুক্ত"}</div>
              </div>
              {isHajj && (
                <div className="mt-5 rounded-xl bg-jannah-green/10 p-4 text-sm text-jannah-ink/80 leading-relaxed">
                  <span className="font-bold text-jannah-green">{en ? "Book with" : "বুকিং:"} ৳ {HAJJ_BOOKING.deposit.toLocaleString("en-IN")}</span> — {HAJJ_BOOKING[lang]}
                </div>
              )}
              <Link to="/customize" className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full btn-gold font-bold uppercase tracking-wider text-sm" data-testid="package-book-btn">
                {t.book_now} <ArrowRight size={16} />
              </Link>
              <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" className="mt-3 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md border-2 border-jannah-green text-jannah-green font-semibold hover:bg-jannah-green/10" data-testid="package-whatsapp-btn">
                <Phone size={16} /> {CONTACT.person} · {CONTACT.phone2}
              </a>
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <div className="max-w-7xl mx-auto px-6 mt-20">
            <h2 className="text-3xl font-bold text-jannah-ink mb-8">{en ? "You May Also Like" : "আপনার পছন্দ হতে পারে"}</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link key={r.id} to={`/package/${r.slug}`} className="card-hover bg-white rounded-xl overflow-hidden border border-jannah-sand block">
                  <img src={r.image} alt="" className="h-40 w-full object-cover" loading="lazy" />
                  <div className="p-5">
                    <h3 className="font-bold text-jannah-green-dark">{en ? r.name_en : r.name_bn}</h3>
                    <div className="mt-1 text-jannah-green font-semibold">{formatPrice(r)}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default PackageDetail;
